import type { UseMutationOptions, UseQueryResult } from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { listingKeys, userKeys } from './keys';
import { getBffQueryData, useInfiniteQueryLPC } from 'app/lib/query';
import {
  addressCreatedAtom,
  babillardReadyAtom,
  bffUserProfileLoadedAtom,
  hasProfileAtom,
  isInsideOnboardingAtom,
  isLoggedInAtom,
  isLoggingOutAtom,
  selfUserLoadedAtom,
  userInfoAtom,
} from 'app/atoms';
import type { FunctionalUseInfiniteQueryOptions, FunctionalUseQueryOptions, Pagination } from 'app/types/network';
import type {
  User,
  UserTypesConfig,
  InvitedUser,
  UpdateUserDTO,
  ChangePasswordDTO,
  CreateUserDTO,
  VerifyUserDTO,
  ForgotPasswordDTO,
  ResetPasswordDTO,
  CreateGuidelinesDTO,
  FindAllUsersDTO,
} from 'app/types/user';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { http } from '../lib/axios';
import { useEffect } from 'react';
import { Currency } from 'app/schemas/user';
import { isNotWhiteLabel, FeatureFlag, isFeatureEnabled } from 'app/utils/helpers';
import { useOnePreference } from './preferences';
import { useNavigation } from 'app/hooks/navigation';
import { useProfile, type Profile } from './profile';
import { useAllAddresses } from './address';
import { storage, StorageKey } from 'app/lib/storage';
import { type TemporaryAddressDTO } from './address-mutations';
import { type AxiosError } from 'axios';
import { useAllGroups } from './groups';
import { logoutCustomerIO } from 'app/hooks/customerIO';
export type { CreateGuidelinesDTO } from 'app/types/user';

export function useUserTypesConfig(opts: FunctionalUseQueryOptions<UserTypesConfig> = {}) {
  const queryKey = userKeys.typeConfig();
  return useQuery<UserTypesConfig>({
    queryKey,
    queryFn: async () => (await http.get<UserTypesConfig>('/users/types')).data,
    ...opts,
    enabled: opts.enabled ?? true,
  });
}

export function useUserMetaPrompt() {
  const userInfo = useAtomValue(userInfoAtom);
  if (!userInfo?.metaPrompts?.length) return false;
  const existingMetaNames = new Set(userInfo.metas?.map((meta) => meta.name));
  for (const mp of userInfo.metaPrompts) {
    if (!existingMetaNames.has(mp.metaName)) {
      return mp;
    }
  }
}

export function useCurrentProfileOffersCount(enabled = true) {
  const isLoggedIn = useAtomValue(isLoggedInAtom);

  return useQuery<number | null>({
    queryKey: [...userKeys.detail('self'), 'current-profile-offers-count'],
    queryFn: async () => (await http.get<User>('/users')).data.offersCount ?? null,
    enabled: isLoggedIn && enabled,
    staleTime: 0,
  });
}

export function useUser(opts: FunctionalUseQueryOptions<User> = {}): UseQueryResult<User> {
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const [selfUserLoaded, setSelfUserLoaded] = useAtom(selfUserLoadedAtom);
  const bffUserProfileLoaded = useAtomValue(bffUserProfileLoadedAtom);
  const userInfo = useAtomValue(userInfoAtom);
  const setUserInfo = useSetAtom(userInfoAtom);

  const client = useQueryClient();
  const queryKey = userKeys.detail('self');

  const query = useQuery<User>({
    queryKey,
    queryFn: async () => {
      const resData =
        (bffUserProfileLoaded ? getBffQueryData<User>(client, queryKey) : undefined) ??
        (await http.get<User>('/users')).data;
      setSelfUserLoaded(true);
      setUserInfo(resData);
      return resData;
    },
    ...opts,
    enabled: isLoggedIn && (opts.enabled ?? true) && !selfUserLoaded && bffUserProfileLoaded,
  });

  // The query cache is persisted, but Jotai atoms are not. On a cold start (notably when opening
  // the app from a push notification), restore userInfo from the hydrated query immediately rather
  // than waiting for the user-profile BFF to run before the rest of the app can identify the user.
  useEffect(() => {
    if (query.data && query.data.id !== userInfo?.id) {
      setUserInfo(query.data);
    }
  }, [query.data, setUserInfo, userInfo?.id]);

  return query;
}

export function useInvitedUsers(opts: FunctionalUseQueryOptions<InvitedUser[]> = {}) {
  return useQuery<InvitedUser[]>({
    queryKey: userKeys.invites(),
    queryFn: () => http.get<InvitedUser[]>('/users/invites').then((res) => res.data),
    ...opts,
  });
}

// Unused; maybe relevant later, eg. allow user to indicate who invited them at any time AFTER they registered
export function useReferredBy(opts: UseMutationOptions<null, Error, UpdateUserDTO> = {}) {
  return useMutation({
    mutationFn: (payload) => http.post<null>('/users/referred-by', payload).then((res) => res.data),
    ...opts,
  });
}

export function useChangePassword(opts: UseMutationOptions<null, Error, ChangePasswordDTO> = {}) {
  return useMutation({
    mutationFn: (payload) => http.post<null>('/users/change-password', payload).then((res) => res.data),
    ...opts,
  });
}

export function useCreateUser(opts: UseMutationOptions<User, Error, CreateUserDTO> = {}) {
  const client = useQueryClient();
  const setSelfUserLoaded = useSetAtom(selfUserLoadedAtom);
  return useMutation<User, Error, CreateUserDTO>({
    mutationFn: (payload) => http.post<User>('/users', payload).then((res) => res.data),
    ...opts,
    onSuccess(result, variables, context) {
      client.setQueryData(userKeys.detail('self'), result);
      client.setQueryData(userKeys.detail(result.id), result);
      setSelfUserLoaded(true);

      if (opts.onSuccess) {
        return opts.onSuccess(result, variables, context);
      }
    },
  });
}

export function useDeleteUser(opts: UseMutationOptions<User> = {}) {
  const setIsLoggingOut = useSetAtom(isLoggingOutAtom);
  return useMutation<User>({
    mutationFn: async () => {
      setIsLoggingOut(true);
      try {
        // Stop using the cached identity before the API anonymizes its Customer.io profile.
        await logoutCustomerIO();
        return (await http.delete<User>(`/users`)).data;
      } catch (error) {
        // Let the authentication watcher identify the still-active user again.
        setIsLoggingOut(false);
        throw error;
      }
    },
    ...opts,
    onSuccess(data, variables, context) {
      if (opts.onSuccess) {
        return opts.onSuccess(data, variables, context);
      }
    },
  });
}

export function useVerifyUser(opts: UseMutationOptions<null, Error, VerifyUserDTO> = {}) {
  return useMutation<null, Error, VerifyUserDTO>({
    mutationFn: (payload) => http.post<null>('/users/verify', payload).then((res) => res.data),
    ...opts,
  });
}

export function useForgotPassword(opts: UseMutationOptions<null, Error, ForgotPasswordDTO> = {}) {
  return useMutation<null, Error, ForgotPasswordDTO>({
    mutationFn: (payload) => http.post<null>('/users/forgot-password', payload).then((res) => res.data),
    ...opts,
  });
}

export function useResetPassword(opts: UseMutationOptions<null, Error, ResetPasswordDTO> = {}) {
  return useMutation<null, Error, ResetPasswordDTO>({
    mutationFn: (payload) => http.post<null>('/users/forgot-password/reset', payload).then((res) => res.data),
    ...opts,
  });
}

export function useOneUserDistance(id: User['id'], opts: FunctionalUseQueryOptions<{ distance: number }> = {}) {
  return useQuery<{ distance: number }>({
    queryKey: userKeys.detail(`distance/${id}`),
    queryFn: () =>
      !id ? { distance: 0 } : http.get<{ distance: number }>(`/users/distance/${id}`).then((res) => res.data),
    ...opts,
  });
}

export function useCreateGuidelines(opts: UseMutationOptions<User, Error, CreateGuidelinesDTO> = {}) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (payload) =>
      http.post<User>('/users/guidelines', payload).then((res) => {
        client.setQueryData(userKeys.detail('self'), res.data);
        client.setQueryData(userKeys.detail(res.data.id), res.data);
        return res.data;
      }),
    ...opts,
  });
}

export function useVerifyRequiredValues(opts: FunctionalUseQueryOptions<Profile, AxiosError> = {}) {
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const [hasProfile, setHasProfile] = useAtom(hasProfileAtom);
  const { data: user } = useUser();
  const navigation = useNavigation();
  const profileQuery = useProfile(user?.id, {
    ...opts,
    enabled: isLoggedIn && !hasProfile && !!user,
  });
  const addressQuery = useAllAddresses();
  const setAddressCreated = useSetAtom(addressCreatedAtom);
  const isInsideOnboarding = useAtomValue(isInsideOnboardingAtom);
  const isBabillardReady = useAtomValue(babillardReadyAtom);
  const userInfo = useAtomValue(userInfoAtom);
  const { data: userTypesConfig } = useUserTypesConfig();
  const allGroups = useAllGroups();
  const { data: sharerType } = useOnePreference('sharerType');
  const userTypeConfig = userInfo?.type ? userTypesConfig?.find((c) => c.name === userInfo.type) : undefined;

  useEffect(() => {
    const redirect = async () => {
      const isNavigationReady = typeof navigation.replace === 'function';
      if (!isNavigationReady || !isBabillardReady || !isLoggedIn || isInsideOnboarding) {
        return;
      }

      if (userTypesConfig && userTypesConfig.length > 0 && !userInfo?.type) {
        console.log('Will ask the user to choose their user type...');
        navigation.navigate('chooseUserType', undefined, { pop: true });
        return;
      }
      if (!addressQuery.data) {
        return;
      }
      if (addressQuery.data.length === 0 || addressQuery.error?.response?.status === 404) {
        const temporaryAddress = await storage.readString(StorageKey.TemporaryAddressUsed);
        const parsedAddress = temporaryAddress ? (JSON.parse(temporaryAddress) as TemporaryAddressDTO) : null;
        if (!parsedAddress) {
          console.log('Will ask the user to complete their profile (address)...');
          navigation.navigate(
            'onboarding',
            {
              screen: 'contact-information',
              params: { hideCross: true },
            },
            { pop: true },
          );
          return;
        }
      }
      setAddressCreated(true);

      if (
        userTypesConfig &&
        allGroups.data?.length === 0 &&
        isFeatureEnabled(FeatureFlag.requireActivationCodeDuringSignup, userTypeConfig)
      ) {
        console.log('Will ask the user to complete their profile (groups)...');
        navigation.navigate('onboarding', { screen: 'credentials', params: { hideCross: true } }, { pop: true });
        return;
      }

      const hasResult = !!profileQuery.data || !!profileQuery.error;
      if (!hasResult) return;

      const notFound = profileQuery.error?.response?.status === 404;
      const profileIncomplete =
        profileQuery.data &&
        ((!profileQuery.data.phone && !user?.acceptedGuidelineDate) ||
          !profileQuery.data.firstName ||
          (isFeatureEnabled(FeatureFlag.lastName, user?.featureFlags) && !profileQuery.data.lastName));

      const shouldRedirect = (notFound || profileIncomplete) && !user?.acceptedGuidelineDate;

      if (shouldRedirect) {
        setHasProfile(false);
        console.log('Will ask the user to complete their personal information...');
        navigation.navigate(
          'onboarding',
          { screen: 'personal-information', params: { hideCross: true } },
          { pop: true },
        );
        return;
      }
      setHasProfile(!notFound && !profileIncomplete);

      if (!sharerType && !user?.acceptedGuidelineDate && isNotWhiteLabel()) {
        console.log('Will ask the user to complete their profile (sharer type)...');
        navigation.navigate(
          'onboarding',
          {
            screen: 'choose-sharer-type',
            params: { hideCross: !user?.acceptedGuidelineDate },
          },
          { pop: true },
        );
        return;
      }
      if (!user?.acceptedGuidelineDate) {
        console.log('Will ask the user to complete their profile (guidelines)...');
        navigation.navigate(
          'onboarding',
          {
            screen: 'guidelines',
            params: { hideCross: true },
          },
          { pop: true },
        );
      }
    };
    void redirect();
  }, [
    addressQuery.data,
    addressQuery.error?.response?.status,
    allGroups.data?.length,
    isBabillardReady,
    isInsideOnboarding,
    isLoggedIn,
    navigation,
    profileQuery.data,
    profileQuery.error,
    setAddressCreated,
    setHasProfile,
    sharerType,
    user?.acceptedGuidelineDate,
    user?.featureFlags,
    userInfo?.type,
    userTypeConfig,
    userTypesConfig,
  ]);
}

export interface FoundUser {
  id: string;
  createdAt: Date;
  lastSeenAt?: Date | null;
  addresses?: { city: string }[];
  profile?: {
    firstName: string;
    lastName: string;
    picture?: string;
  };
  memberships?: {
    groupId?: string;
    group: {
      id?: string;
      name: string;
      picture?: null | string;
    };
  }[];
  distance?: number;
  trustPoints?: number | null;
  canReceiveInAppContact?: boolean;
}

export function useFindUsers(
  filters?: FindAllUsersDTO,
  opts: FunctionalUseInfiniteQueryOptions<Pagination<FoundUser>> = {},
) {
  const client = useQueryClient();
  return useInfiniteQueryLPC<Pagination<FoundUser>>({
    queryKey: userKeys.list(filters),
    queryFn: async ({ pageParam }) => {
      const data = (
        await http.get<Pagination<FoundUser>>('/users/find', {
          params: { ...filters, take: 25, skip: pageParam },
        })
      ).data;
      data.results.forEach((user) => {
        client.setQueryData(userKeys.detail(user.id), user);
      });
      return data;
    },
    ...opts,
    initialPageParam: 0,
    getNextPageParam: (previousPage) => {
      if (previousPage.skip + previousPage.take < previousPage.total) {
        return previousPage.skip + previousPage.take;
      }

      return undefined;
    },
  });
}

export function useUserIsInsured() {
  const { data: user } = useUser();
  return user?.insured;
}

export function useUserCanBeInsured() {
  const { data: user } = useUser();
  return Boolean(user?.canBeInsured);
}

export function useUserCurrency() {
  const { data: user } = useUser();
  return user?.currency ?? Currency.CAD;
}

export function useUpsertUserMeta(opts: UseMutationOptions<User, Error, { name: string; value: string }> = {}) {
  const setUserInfo = useSetAtom(userInfoAtom);
  const client = useQueryClient();
  return useMutation<User, Error, { name: string; value: string }>({
    mutationFn: ({ name, value }) => {
      return http.patch<User>(`/users/metas/${name}`, { value }).then((res) => {
        client.setQueryData(userKeys.detail('self'), res.data);
        setUserInfo(res.data);
        return res.data;
      });
    },
    ...opts,
  });
}

export function useReportUser(opts: UseMutationOptions<unknown, Error, User['id']> = {}) {
  const queryClient = useQueryClient();
  return useMutation<unknown, Error, User['id']>({
    mutationFn: (userId) => http.post<unknown>(`/users/${userId}/report`).then((res) => res.data),
    ...opts,
    onSuccess: async (data, variables, context) => {
      queryClient.removeQueries({ queryKey: listingKeys.lists() });
      await queryClient.invalidateQueries({ queryKey: listingKeys.all });
      return opts.onSuccess?.(data, variables, context);
    },
  });
}
